import { useEffect, useState } from "react";
import { X, Info, ChevronDown, Check } from "lucide-react";
import SAPCongratulations from "./SAPCongratulations";
import { authFetch } from "../../../../api";
import SAPNotEligibleModal from "./SAPNotEligibleModal";

export default function EligibilityModal({ course }) {
  const userId = localStorage.getItem("userId");
  const [workExperienceGap, setWorkExperienceGap] = useState("");
  const [totalWorkExperience, setTotalWorkExperience] = useState("");
  const [studyGapDuration, setStudyGapDuration] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const [openCongrats, setOpenCongrats] = useState(false);
  const [openNotEligible, setOpenNotEligible] = useState(false);
  const [notEligibleMessage, setNotEligibleMessage] = useState("");

  const handleClose = () => setIsOpen(false);

  const handleMoveForward = () => {
    // Close current modal and open Congratulations modal
    setIsOpen(false);
    setOpenCongrats(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await authFetch(
      "application/save-user-experience-details",
      {
        method: "POST",
        body: JSON.stringify({
          user_id: userId,
          workExperienceGap,
          totalWorkExperience,
          studyGapDuration,
          course_id: course.id,
        }),
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = await response.json();

    if (response.status === 201 || response.status === 200) {
      setIsOpen(false);
      handleMoveForward();
    } else if (response.status === 400) {
      setIsOpen(false);
      setOpenNotEligible(true);
      setNotEligibleMessage(data.message);
    }
  };

  const getExperienceData = async () => {
    const response = await authFetch(
      "application/show-user-experience-details",
      {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
        headers: { "Content-Type": "application/json" },
      }
    );

    const data = await response.json();

    if (response.status === 200) {
      setWorkExperienceGap(data.data.work_experience_gap || "");
      setTotalWorkExperience(data.data.total_work_experience || "");
      setStudyGapDuration(data.data.study_gap_duration || "");
    }
  };

  useEffect(() => {
    getExperienceData();
  }, []);

  return (
    <>
      {/* Eligibility Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[1100] bg-black/50">
          <div className="bg-white mt-20 rounded-2xl shadow-lg max-w-xl w-full p-8 relative">
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-[#572575] transition-colors"
              aria-label="Close"
            >
              <X size={22} />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="p-1 rounded-full">
                <img src="/PremiumAccess/EligibilityModal.png" alt="square" />
              </div>
            </div>

            <h2 className="font-siromi text-[#8A40B5] text-[22px] font-normal text-center leading-[14px]">
              Additional Required Information
            </h2>
            <p className="font-altis mt-4 text-[#444444] text-[16px] font-normal leading-[16px] text-center mb-6">
              Before moving ahead, let’s quickly check your eligibility.
            </p>

            <div
              className={`space-y-4 mb-6 p-4 rounded-lg ${
                workExperienceGap && totalWorkExperience && studyGapDuration
                  ? "bg-[#F9F0F8]"
                  : "bg-[#F9F9F9]"
              }`}
            >
              {/* Study Gap Duration */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Study Gap Duration
                </label>
                <div className="relative">
                  <select
                    value={studyGapDuration}
                    onChange={(e) => setStudyGapDuration(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg appearance-none bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select Your Work Experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5+">5+ years</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-3.5 text-gray-400 pointer-events-none"
                    size={20}
                  />
                </div>
              </div>
              {/* Total Work Experience */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Total Work Experience
                </label>
                <div className="relative">
                  <select
                    value={totalWorkExperience}
                    onChange={(e) => setTotalWorkExperience(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg appearance-none bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select Your Work Experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5+">5+ years</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-3.5 text-gray-400 pointer-events-none"
                    size={20}
                  />
                </div>
              </div>
              {/* Work Experience Gap */}
              {totalWorkExperience && (
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Work Experience Gap
                  </label>
                  <div className="relative">
                    <select
                      value={workExperienceGap}
                      onChange={(e) => setWorkExperienceGap(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg appearance-none bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select Your Work Experience Gap</option>
                      <option value="0-6">0-6 months</option>
                      <option value="6-12">6-12 months</option>
                      <option value="1-2">1-2 years</option>
                      <option value="2+">2+ years</option>
                    </select>
                    <ChevronDown
                      className="absolute right-3 top-3.5 text-gray-400 pointer-events-none"
                      size={20}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-6 flex items-center justify-center gap-3">
              <Info size={20} className="text-blue-500 mt-0.5" />
              <p className="text-sm text-blue-600">
                All fields are required for eligibility check
              </p>
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-200 text-[#572575] rounded-lg font-altis font-bold text-[14px] leading-[14px] flex items-center justify-center hover:bg-gray-300 transition"
              >
                <X className="mr-1" /> Not Yet
              </button>

              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#572575] text-white rounded-lg font-altis font-bold text-[14px] leading-[14px] flex items-center gap-2 hover:bg-[#572575] transition"
              >
                <Check /> Yes, Move Forward
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Congratulations Modal (rendered independently) */}
      {openCongrats && (
        <SAPCongratulations onClose={() => setOpenCongrats(false)} />
      )}

      {openNotEligible && (
        <SAPNotEligibleModal
          notEligibleMessage={notEligibleMessage}
          onClose={() => setOpenNotEligible(false)}
        />
      )}
    </>
  );
}
